import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import qs from "qs";
import { setLoggedIn } from "./loginSlice"; // keep existing slice
import { setCompany } from "./companySlice"; // keep existing slice

const initialState = {
    value: {},
    status: "idle",          // 'idle' | 'loading' | 'succeeded' | 'failed'
    error: null,
};

// 🔹 Hydrate account from /account/login_persistence
export const hydrateAccountFromPersistence = createAsyncThunk(
    "account/hydrateFromPersistence",
    async (_, { dispatch, rejectWithValue }) => {
        try {
            const base = process.env.REACT_APP_BASE_SERVER;
            const signedInKey = `${new URL(base).hostname}_signedIn`;
            const cookie = document.cookie
                .split("; ")
                .find((c) => c.startsWith(`${signedInKey}=`));

            if (!cookie || !cookie.endsWith("=true")) {
                // no token, go straight to login
                const redirectUrl = [
                    process.env.REACT_APP_ACCOUNT_URL,
                    "/login?",
                    `redirect_=${encodeURIComponent(window.location.href)}`,
                ].join("");
                window.location.assign(redirectUrl);
                return rejectWithValue({ code: "NO_TOKEN" });
            }

            const res = await axios({
                withCredentials: true,
                method: "POST",
                headers: { "content-type": "application/x-www-form-urlencoded" },
                data: qs.stringify({}),
                url: `${base}/account/login_persistence`,
            });

            if (res.data?.success) {
                const row = res.data.data?.[0] ?? {};
                localStorage.setItem("name", row.user_name ?? "");
                localStorage.setItem("company", row.client_name ?? "");

                const company = {
                    id: row.user_client,
                    username: row.user_name,
                    company: row.client_name,
                    onboard: row.client_onboard,
                };
                dispatch(setCompany(company));
                dispatch(setLoggedIn(true));
                return row;
            } else {
                dispatch(setLoggedIn(false));
                return rejectWithValue({ code: "NOT_LOGGED_IN" });
            }
        } catch (err) {
            const status = err?.response?.status;
            const serverMessage = err?.response?.data?.message;
            dispatch(setLoggedIn(false));

            if (status === 401) {
                return rejectWithValue({ code: "UNAUTHORIZED", message: serverMessage || "token is missing" });
            }
            if (status === 403) {
                // Backend denies trucount access (e.g. operational admin without
                // SUPERADMIN/CONSULTANT override). Cookie is intentionally preserved
                // so the same session works on the admin panel — redirect there.
                if (process.env.REACT_APP_ADMIN_URL) {
                    window.location.assign(process.env.REACT_APP_ADMIN_URL);
                }
                return rejectWithValue({ code: "FORBIDDEN", message: serverMessage || "access restricted" });
            }
            return rejectWithValue({ code: "BACKEND_DOWN", message: serverMessage || err?.message || "Login persistence failed" });
        }
    }
);

export const accountSlice = createSlice({
    name: "account",
    initialState,
    reducers: {
        setAccount: (state, action) => {
            state.value = action.payload ?? {};
        },
        setHasTours: (state, action) => {
            const tour = action.payload;
            if (!tour) return;
            if (!Array.isArray(state.value.tours)) state.value.tours = [];
            if (!state.value.tours.includes(tour)) state.value.tours.push(tour);
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(hydrateAccountFromPersistence.pending, (state) => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(hydrateAccountFromPersistence.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.value = action.payload ?? {};
            })
            .addCase(hydrateAccountFromPersistence.rejected, (state, action) => {
                state.status = "failed";
                state.value = {};
                state.error = action.payload || action.error?.message || "Failed";
            })
    },
});

export const { setAccount, setHasTours } = accountSlice.actions;

// 🔹 Selectors
export const selectAccount = (state) => state.account.value || {};
export const selectIsPremium = (state) => (state.account.value?.client_subscription === 2);
export const selectAccountClientSubscription = (state) => (state.account.value?.client_subscription);
export const selectAccountStatus = (state) => state.account.status;

export default accountSlice.reducer;
